import { Value } from "@bufbuild/protobuf";
import { Map, OrderedMap } from "immutable";
import { z } from "zod";

import { type TableMutation } from "../QueryEvaluator/TableMutation";
import { Tuple, rawTupleSchema } from "../Tuple";

export interface ZTuple<T> {
	tuple: Tuple<T>;
	weight: number;
}

export function zTuple<T>(id: string, value: T | Tuple<T>, weight: number): ZTuple<T> {
	return { tuple: value instanceof Tuple ? value : Tuple.from<T>(id, value), weight };
}

export class ZSet<T> implements Iterable<ZTuple<T>> {
	private _tuples: OrderedMap<string, ZTuple<T>>;
	private _byId: Map<string, Tuple<T>>;

	constructor(tuples: OrderedMap<string, ZTuple<T>>, byId: Map<string, Tuple<T>>) {
		this._tuples = tuples;
		this._byId = byId;
	}

	[Symbol.iterator](): Iterator<ZTuple<T>> {
		return this._tuples.values();
	}

	static empty<T>(): ZSet<T> {
		return new ZSet<T>(OrderedMap(), Map());
	}

	static from<T>(tuples: ZTuple<T>[]): ZSet<T> {
		let tuplesMap = OrderedMap<string, ZTuple<T>>();
		let byIdMap = Map<string, Tuple<T>>();
		for (const tuple of tuples) {
			tuplesMap = tuplesMap.set(tuple.tuple.contentId(), tuple);
			byIdMap = byIdMap.set(tuple.tuple.id, tuple.tuple);
		}
		return new ZSet(tuplesMap, byIdMap);
	}

	get size(): number {
		return this._tuples.size;
	}

	deltaFromMutations(mutation: TableMutation<T>): ZSet<T> {
		let delta = ZSet.empty<T>();
		switch (mutation.type) {
			case "INSERT":
				delta = delta.add(zTuple(mutation.recordId, mutation.record.getValue(), 1));
				break;
			case "UPDATE": {
				const prevTuple = this._byId.get(mutation.recordId);
				if (!prevTuple) {
					throw new Error(`Tuple ${mutation.recordId} not found`);
				}
				delta = delta.add(zTuple<T>(mutation.recordId, prevTuple, -1));
				delta = delta.add(
					zTuple(mutation.recordId, prevTuple.applyPatch([mutation.change]), 1)
				);
				break;
			}
			case "DELETE": {
				const prevTuple = this._byId.get(mutation.recordId);
				if (!prevTuple) {
					throw new Error(`Tuple ${mutation.recordId} not found`);
				}
				delta = delta.add(zTuple(mutation.recordId, prevTuple, -1));
				break;
			}
		}
		return delta;
	}

	sort<K>(key: (tuple: Tuple<T>) => Tuple<K>, direction: "ASC" | "DESC"): ZSet<T> {
		return new ZSet(
			this._tuples.sort((a, b) => {
				const aKey = key(a.tuple);
				const bKey = key(b.tuple);
				if (direction === "ASC") {
					return aKey.comparator(bKey);
				} else {
					return bKey.comparator(aKey);
				}
			}),
			this._byId
		);
	}

	merge(other: ZSet<T>): ZSet<T> {
		return new ZSet(this._tuples.merge(other._tuples), this._byId.merge(other._byId));
	}

	clone(): ZSet<T> {
		return new ZSet(this._tuples, this._byId);
	}

	toArray(): ZTuple<T>[] {
		return this._tuples.valueSeq().toArray();
	}

	weight(tuple: Tuple<T>): number {
		return this._tuples.get(tuple.contentId())?.weight ?? 0;
	}

	add(ztuple: ZTuple<T>): ZSet<T> {
		const { tuple, weight } = ztuple;
		if (weight === 0) {
			return this;
		}

		const newZSet = this.clone();
		const contentId = tuple.contentId();
		const currentTuple = newZSet._tuples.get(contentId, { tuple, weight: 0 });
		const nextWeight = currentTuple.weight + weight;

		if (nextWeight === 0) {
			newZSet._tuples = newZSet._tuples.delete(contentId);
			newZSet._byId = newZSet._byId.delete(tuple.id);
		} else {
			newZSet._tuples = newZSet._tuples.set(contentId, { tuple, weight: nextWeight });
			newZSet._byId = newZSet._byId.set(tuple.id, tuple);
		}

		return newZSet;
	}

	has(tuple: Tuple<T>): boolean {
		return this._tuples.has(tuple.contentId());
	}

	hasId(id: string): boolean {
		return this._byId.has(id);
	}

	take(n: number | undefined): ZSet<T> {
		if (n === undefined) {
			return this;
		}

		const takenTuples = this._tuples.take(n);
		let takenById = Map<string, Tuple<T>>();
		for (const tuple of takenTuples.values()) {
			takenById = takenById.set(tuple.tuple.id, tuple.tuple);
		}
		return new ZSet(takenTuples, takenById);
	}

	filter(predicate: (tuple: ZTuple<T>) => boolean): ZSet<T> {
		let filteredTuples = OrderedMap<string, ZTuple<T>>();
		let filteredById = Map<string, Tuple<T>>();

		for (const tuple of this) {
			if (predicate(tuple)) {
				filteredTuples = filteredTuples.set(tuple.tuple.contentId(), tuple);
				filteredById = filteredById.set(tuple.tuple.id, tuple.tuple);
			}
		}
		return new ZSet(filteredTuples, filteredById);
	}

	arrange<K>(key: (tuple: Tuple<T>) => Tuple<K>): Arrangement<T> {
		let grouped = OrderedMap() satisfies Arrangement<T>;
		for (const tuple of this) {
			const keyTuple = key(tuple.tuple);
			const contentId = keyTuple.contentId();
			const keySet = grouped.get(contentId, ZSet.empty<T>());
			keySet.add(tuple);
			grouped = grouped.set(contentId, keySet);
		}
		return grouped;
	}
}

export type Arrangement<T> = OrderedMap<string, ZSet<T>>;

//
// Raw ZTuple schemas.
//

export const rawZTupleSchema = z.object({
	tuple: rawTupleSchema.optional(),
	weight: z.number(),
});
export type RawZTuple = z.infer<typeof rawZTupleSchema>;

export const hydrateZTuple = <T>(rawZTuple: RawZTuple): ZTuple<T> => {
	if (!rawZTuple.tuple) {
		throw new Error("Raw ZTuple has no tuple");
	}
	if (!rawZTuple.tuple.id) {
		throw new Error("Raw ZTuple has no id");
	}
	if (!rawZTuple.tuple.value) {
		throw new Error("Raw ZTuple has no value");
	}
	const value: T =
		rawZTuple.tuple.value instanceof Value
			? (rawZTuple.tuple.value.toJson() as T)
			: (rawZTuple.tuple.value as T);
	return {
		tuple: Tuple.from<T>(rawZTuple.tuple.id, value as T),
		weight: rawZTuple.weight,
	};
};

export const dehydrateZTuple = <T>(ztuple: ZTuple<T>): RawZTuple => ({
	tuple: { id: ztuple.tuple.id, value: ztuple.tuple.getValue() },
	weight: ztuple.weight,
});

//
// Raw ZSet schemas.
//

export const rawZSetSchema = z.object({
	tuples: z.array(rawZTupleSchema),
});
export type RawZSet = z.infer<typeof rawZSetSchema>;

export const hydrateZSet = <T>(rawZSet: RawZSet): ZSet<T> =>
	ZSet.from<T>(rawZSet.tuples.map(hydrateZTuple<T>));

export const dehydrateZSet = <T>(zset: ZSet<T>): RawZSet => ({
	tuples: zset.toArray().map(dehydrateZTuple),
});

//
// Raw Arrangement schemas.
//

export const rawArrangementSchema = z.record(z.string(), rawZSetSchema);
export type RawArrangement = z.infer<typeof rawArrangementSchema>;

export const hydrateArrangement = <T>(rawArrangement: RawArrangement): Arrangement<T> => {
	let hydrated = OrderedMap() satisfies Arrangement<T>;
	for (const [key, value] of Object.entries(rawArrangement)) {
		hydrated = hydrated.set(key, hydrateZSet<T>(value));
	}
	return hydrated;
};

export const dehydrateArrangement = <T>(arrangement: Arrangement<T>): RawArrangement => {
	const rawArrangement = Object.fromEntries(
		Array.from(arrangement.entries()).map(([key, value]) => [key, dehydrateZSet(value)])
	);
	return rawArrangement;
};
